import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FlexBox, FlexBoxDirection, Input, Label, WrappingType } from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useABAPQueryStore } from 'stores/CreateABAPQueryStore';
import { validateString } from 'utils/validator';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { IssueType, useCreateAbapMutation } from 'generated/graphql';
import isMatch from 'lodash/isMatch';
import { commonABAPQueryInitialStateFields } from 'stores/slices/ABAPQuerySlices/CommonABAPQuerySlice';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

export const ABAPQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const store = useABAPQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateAbapMutation();

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return validateAllFields();
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  const validateSystem = () => {
    return validateString(store.system, store.setSystemValueState);
  };
  const validateObjectType = () => {
    return validateString(store.objectType, store.setObjectTypeValueState);
  };
  const validateObjectName = () => {
    return validateString(store.objectName, store.setObjectNameValueState);
  };
  const validateCollectionPackage = () => {
    return validateString(store.collectionPackage, store.setCollectionPackageValueState);
  };
  const validateApplicationComponent = () => {
    return validateString(store.applicationComponent, store.setApplicationComponentValueState);
  };
  const validateRepositoryProgramType = () => {
    return validateString(store.repositoryProgramType, store.setRepositoryProgramTypeValueState);
  };
  const validateRepositoryProgramId = () => {
    return validateString(store.repositoryProgramId, store.setRepositoryProgramIdValueState);
  };
  const validateRepositoryProgramName = () => {
    return validateString(store.repositoryProgramName, store.setRepositoryProgramNameValueState);
  };
  const validateTranslationArea = () => {
    return validateString(store.translationArea, store.setTranslationAreaValueState);
  };

  const validateAllFields = () => {
    const isSystemValid = validateSystem();
    const isObjectTypeValid = validateObjectType();
    const isObjectNameValid = validateObjectName();
    const isCollectionPackageValid = validateCollectionPackage();
    const isApplicationComponentValid = validateApplicationComponent();
    const isRepositoryProgramTypeValid = validateRepositoryProgramType();
    const isRepositoryProgramIdValid = validateRepositoryProgramId();
    const isRepositoryProgramNameValid = validateRepositoryProgramName();
    const isTranslationAreaValid = validateTranslationArea();
    return (
      isSystemValid &&
      isObjectTypeValid &&
      isObjectNameValid &&
      isCollectionPackageValid &&
      isApplicationComponentValid &&
      isRepositoryProgramTypeValid &&
      isRepositoryProgramIdValid &&
      isRepositoryProgramNameValid &&
      isTranslationAreaValid
    );
  };

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProject,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        system: store.system,
        objectType: store.objectType,
        objectName: store.objectName,
        objectOwner: store.objectOwner,
        collectionPackage: store.collectionPackage,
        collectionOwner: store.collectionOwner,
        languagesTranslatorTeams: store.languagesTranslatorTeams,
        applicationComponent: store.applicationComponent,
        repositoryProgramType: store.repositoryProgramType,
        repositoryProgramId: store.repositoryProgramId,
        repositoryProgramName: store.repositoryProgramName,
        translationArea: store.translationArea,
        maxLength: store.maxLength
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, commonABAPQueryInitialStateFields);
  };

  const onChangeSystem = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setSystem(value);
    validateString(value, store.setSystemValueState);
  };
  const onChangeObjectType = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setObjectType(value);
    validateString(value, store.setObjectTypeValueState);
  };
  const onChangeObjectName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setObjectName(value);
    validateString(value, store.setObjectNameValueState);
  };
  const onChangeObjectOwner = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setObjectOwner(value);
  };
  const onChangeCollectionPackage = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setCollectionPackage(value);
    validateString(value, store.setCollectionPackageValueState);
  };
  const onChangeCollectionOwner = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setCollectionOwner(value);
  };
  const onChangeMaxLength = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setMaxLength(value != '' ? Number(value) : null);
  };
  const onChangeApplicationComponent = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setApplicationComponent(value);
    validateString(value, store.setApplicationComponentValueState);
  };
  const onChangeRepositoryProgramType = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setRepositoryProgramType(value);
    validateString(value, store.setRepositoryProgramTypeValueState);
  };
  const onChangeRepositoryProgramId = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setRepositoryProgramId(value);
    validateString(value, store.setRepositoryProgramIdValueState);
  };
  const onChangeRepositoryProgramName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setRepositoryProgramName(value);
    validateString(value, store.setRepositoryProgramNameValueState);
  };
  const onChangeTranslationArea = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setTranslationArea(value);
    validateString(value, store.setTranslationAreaValueState);
  };

  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="system-label" className={classes.label} showColon required>
            <span data-help-id="create-query-ABAP-system">
              {t('display-query-details-abap-system')}
            </span>
          </Label>
          <Input
            aria-labelledby="system-label"
            valueState={store.systemValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-system')}
              </Label>
            }
            onChange={onChangeSystem}
            className={classes.formInput}
            value={store.system}
            disabled={freezeStore.system}
          ></Input>
          <Label
            id="translation-area-label"
            className={classes.secondColumnLabel}
            showColon
            required
          >
            <span data-help-id="create-query-ABAP-translation-area">
              {t('display-query-details-abap-translation-area')}
            </span>
          </Label>
          <Input
            aria-labelledby="translation-area-label"
            valueState={store.translationAreaValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-translation-area')}
              </Label>
            }
            onChange={onChangeTranslationArea}
            className={classes.formInput}
            value={store.translationArea}
            disabled={freezeStore.translationArea}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="object-type-label" className={classes.label} showColon required>
            <span data-help-id="create-query-ABAP-object-type">
              {t('display-query-details-abap-object-type')}
            </span>
          </Label>
          <Input
            aria-labelledby="object-type-label"
            valueState={store.objectTypeValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-object-type')}
              </Label>
            }
            onChange={onChangeObjectType}
            className={classes.formInput}
            value={store.objectType}
            disabled={freezeStore.objectType}
          ></Input>
          <Label id="object-name-label" className={classes.secondColumnLabel} showColon required>
            <span data-help-id="create-query-ABAP-object-name">
              {t('display-query-details-abap-object-name')}
            </span>
          </Label>
          <Input
            aria-labelledby="object-name-label"
            valueState={store.objectNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-object-name')}
              </Label>
            }
            onChange={onChangeObjectName}
            className={classes.formInput}
            value={store.objectName}
            disabled={freezeStore.objectName}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="collection-package-label" className={classes.label} showColon required>
            <span data-help-id="create-query-ABAP-collection-package">
              {t('display-query-details-abap-collection-package')}
            </span>
          </Label>
          <Input
            aria-labelledby="collection-package-label"
            valueState={store.collectionPackageValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-collection-package')}
              </Label>
            }
            onChange={onChangeCollectionPackage}
            className={classes.formLineInput}
            value={store.collectionPackage}
            disabled={freezeStore.collectionPackage}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="application-component-label" className={classes.label} showColon required>
            <span data-help-id="create-query-ABAP-application-component">
              {t('display-query-details-abap-application-component')}
            </span>
          </Label>
          <Input
            aria-labelledby="application-component-label"
            valueState={store.applicationComponentValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-application-component')}
              </Label>
            }
            onChange={onChangeApplicationComponent}
            className={classes.formInput}
            value={store.applicationComponent}
            disabled={freezeStore.applicationComponent}
          ></Input>
          <Label
            id="repository-program-id-label"
            className={classes.secondColumnLabel}
            showColon
            required
          >
            <span data-help-id="create-query-ABAP-repository-program-id">
              {t('display-query-details-abap-repository-program-id')}
            </span>
          </Label>
          <Input
            aria-labelledby="repository-program-id-label"
            valueState={store.repositoryProgramIdValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-repository-program-id')}
              </Label>
            }
            onChange={onChangeRepositoryProgramId}
            className={classes.formInput}
            value={store.repositoryProgramId}
            disabled={freezeStore.repositoryProgramId}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="repository-program-type-label" className={classes.label} showColon required>
            <span data-help-id="create-query-ABAP-repository-program-type">
              {t('display-query-details-abap-repository-program-type')}
            </span>
          </Label>
          <Input
            aria-labelledby="repository-program-type-label"
            valueState={store.repositoryProgramTypeValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-repository-program-type')}
              </Label>
            }
            onChange={onChangeRepositoryProgramType}
            className={classes.formInput}
            value={store.repositoryProgramType}
            disabled={freezeStore.repositoryProgramType}
          ></Input>
          <Label
            id="repository-program-name-label"
            className={classes.secondColumnLabel}
            showColon
            required
          >
            <span data-help-id="create-query-ABAP-repository-program-name">
              {t('display-query-details-abap-repository-program-name')}
            </span>
          </Label>
          <Input
            aria-labelledby="repository-program-name-label"
            valueState={store.repositoryProgramNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-repository-program-name')}
              </Label>
            }
            onChange={onChangeRepositoryProgramName}
            className={classes.formInput}
            value={store.repositoryProgramName}
            disabled={freezeStore.repositoryProgramName}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="object-owner-label" className={classes.label} showColon>
            <span data-help-id="create-query-ABAP-object-owner">
              {t('display-query-details-abap-object-owner')}
            </span>
          </Label>
          <Input
            aria-labelledby="object-owner-label"
            valueState={store.objectOwnerValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-object-owner')}
              </Label>
            }
            onChange={onChangeObjectOwner}
            className={classes.formInput}
            value={store.objectOwner ?? ''}
            disabled={freezeStore.objectOwner}
          ></Input>
          <Label id="collection-owner-label" className={classes.secondColumnLabel} showColon>
            <span data-help-id="create-query-ABAP-collection-owner">
              {t('display-query-details-abap-collection-owner')}
            </span>
          </Label>
          <Input
            aria-labelledby="collection-owner-label"
            valueState={store.collectionOwnerValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-collection-owner')}
              </Label>
            }
            onChange={onChangeCollectionOwner}
            className={classes.formInput}
            value={store.collectionOwner ?? ''}
            disabled={freezeStore.collectionOwner}
          ></Input>
        </div>
      </FlexBox>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label id="max-length-label" className={classes.label} showColon>
            <span data-help-id="create-query-ABAP-max-length">
              {t('display-query-details-abap-max-length')}
            </span>
          </Label>
          <Input
            aria-labelledby="max-length-label"
            onChange={onChangeMaxLength}
            className={classes.formInput}
            type="Number"
            value={String(store.maxLength ?? '')}
            disabled={freezeStore.maxLength}
          ></Input>
        </div>
      </FlexBox>
    </>
  );
});
